// frontend/src/widgets/CameraJobBindingWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-04-20
// Updated:      2026-09-23 15:33 BST
//
// Description:
//   Camera job binding widget for the Cameras page.
//   Responsibilities:
//     - List tenant cameras
//     - Show active / scheduled / closed camera ↔ job bindings
//     - Show recent binding history
//     - Allow quick bind / rebind from the card
//     - Allow full binding edit via modal
//     - Allow explicit unbind while preserving audit history
//
// Mobile Responsiveness (2026-09-07):
//   - Desktop keeps the original full two-part camera cards unchanged in behaviour.
//   - Mobile uses compact camera summary cards in the normal page scroll; there is
//     no nested fixed-height camera scroller and no camera-card scroll snapping.
//   - Each mobile card shows core device identity plus a concise current-binding
//     summary and a Binding Details action.
//   - Binding Details opens a mobile flyover containing the full current binding,
//     quick bind/rebind controls, recent binding history, and Edit Binding action.
//   - The flyover reads the already-loaded camera/binding/job state; it does not
//     introduce duplicate API calls or a second data-fetch path.
//   - Recent binding history becomes labelled cards inside the mobile flyover.
//   - The edit modal remains near-full-screen on mobile with stacked date fields
//     and touch-sized actions.
//   - The nearest ReactGridLayout host expands to the natural mobile list height,
//     so every camera remains reachable through normal page scrolling.
//
// Notes:
//   - Uses ANPR-only job options lookup for dropdown selection.
//   - Job lookup excludes terminal / non-operational jobs.
//   - Quick bind uses "from now" semantics on the card/flyover.
//   - Edit modal supports future scheduling, end date, notes, and unbind.
//   - Current backend supports create / update / unbind and preserves history.
//   - Camera/binding loading keeps the existing per-camera binding lookup behaviour;
//     this responsiveness pass does not alter backend request architecture.
//   - 2026-09-23 deployment-location update:
//       • Camera bindings now expose binding-specific latitude / longitude.
//       • Edit Binding can set coordinates manually or from browser geolocation.
//       • Binding coordinates are intentionally independent of the job location and
//         the camera master location.
//       • Binding coordinates are shown in current-binding and recent-history views.
//       • Quick Bind / Rebind remains lightweight; deployment coordinates can be
//         added immediately afterwards through Edit Binding.

import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";

const JOB_OPTIONS_LIMIT = 100;
const MOBILE_BREAKPOINT = 768;

const mobileLabelStyle = {
  fontSize: "0.72rem",
  fontWeight: 800,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.035em",
  lineHeight: 1.25,
};

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

const toDatetimeLocalValue = (value) => {
  if (!value) return "";
  try {
    const d = new Date(value);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
};

const fromDatetimeLocalValue = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toISOString();
  } catch {
    return null;
  }
};

const formatCoordinate = (value) => {
  if (value === null || value === undefined || value === "") return "—";

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";

  return numeric.toFixed(6);
};

const formatBindingLocation = (row) => {
  const hasLatitude =
    row?.latitude !== null &&
    row?.latitude !== undefined &&
    row?.latitude !== "";
  const hasLongitude =
    row?.longitude !== null &&
    row?.longitude !== undefined &&
    row?.longitude !== "";

  if (!hasLatitude || !hasLongitude) return "—";

  return `${formatCoordinate(row.latitude)}, ${formatCoordinate(row.longitude)}`;
};

const getBindingStatus = (row) => {
  const now = new Date();
  const activeFrom = row?.active_from ? new Date(row.active_from) : null;
  const activeTo = row?.active_to ? new Date(row.active_to) : null;
  const isActive = Boolean(row?.is_active);

  if (isActive && activeFrom && activeFrom > now) {
    return "Scheduled";
  }

  if (isActive && (!activeTo || activeTo > now)) {
    return "Active";
  }

  return "Closed";
};

const getStatusPillStyle = (status) => {
  if (status === "Active") {
    return {
      backgroundColor: "#ecfdf5",
      border: "1px solid #a7f3d0",
      color: "#065f46",
    };
  }

  if (status === "Scheduled") {
    return {
      backgroundColor: "#eff6ff",
      border: "1px solid #bfdbfe",
      color: "#1d4ed8",
    };
  }

  return {
    backgroundColor: "#f3f4f6",
    border: "1px solid #d1d5db",
    color: "#4b5563",
  };
};

const buttonStyle = (theme, variant = "action", disabled = false) => ({
  backgroundColor:
    variant === "general"
      ? theme.button_general?.normal || "#4b5563"
      : theme.button_action?.normal || "#000",
  color: "#fff",
  padding: "10px 14px",
  borderRadius: "8px",
  border: "none",
  fontWeight: 700,
  fontFamily: "var(--font-body)",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.7 : 1,
  whiteSpace: "nowrap",
});

const applyHover = (e, theme, variant = "action", disabled = false) => {
  if (disabled) return;
  e.currentTarget.style.backgroundColor =
    variant === "general"
      ? theme.button_general?.hover || "#374151"
      : theme.button_action?.hover || "#333";
};

const applyNormal = (e, theme, variant = "action") => {
  e.currentTarget.style.backgroundColor =
    variant === "general"
      ? theme.button_general?.normal || "#4b5563"
      : theme.button_action?.normal || "#000";
};

const applyClick = (e, theme, variant = "action", disabled = false) => {
  if (disabled) return;
  e.currentTarget.style.backgroundColor =
    variant === "general"
      ? theme.button_general?.click || "#1f2937"
      : theme.button_action?.click || "#111";
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: "8px",
  border: "1px solid #d1d5db",
  outline: "none",
  fontFamily: "var(--font-body)",
  backgroundColor: "#fff",
};

const textareaStyle = {
  ...inputStyle,
  minHeight: "88px",
  resize: "vertical",
};

const modalOverlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  zIndex: 9999,
};

const modalCardStyle = {
  width: "100%",
  maxWidth: "760px",
  maxHeight: "90vh",
  overflowY: "auto",
  backgroundColor: "#fff",
  borderRadius: "16px",
  boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
  border: "1px solid #e5e7eb",
  padding: "18px",
};

const EMPTY_MODAL_FORM = {
  job_id: "",
  active_from: "",
  active_to: "",
  latitude: "",
  longitude: "",
  notes: "",
};

const CameraJobBindingWidget = () => {
  const { theme } = useTheme();

  const [cameras, setCameras] = useState([]);
  const [bindingsByCamera, setBindingsByCamera] = useState({});
  const [jobOptions, setJobOptions] = useState([]);
  const [jobOptionsLoading, setJobOptionsLoading] = useState(false);
  const [quickJobInputs, setQuickJobInputs] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingCameraId, setSavingCameraId] = useState(null);
  const [modalSaving, setModalSaving] = useState(false);
  const [modalUnbinding, setModalUnbinding] = useState(false);
  const [locationBusy, setLocationBusy] = useState(false);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [selectedBinding, setSelectedBinding] = useState(null);
  const [modalForm, setModalForm] = useState(EMPTY_MODAL_FORM);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : false
  );
  const [mobileDetailsCameraId, setMobileDetailsCameraId] = useState(null);

  const rootRef = useRef(null);

  const loadJobOptions = async () => {
    setJobOptionsLoading(true);
    try {
      const res = await axiosInstance.get(
        `anpr/cameras/jobs/anpr-options?limit=${JOB_OPTIONS_LIMIT}`
      );
      setJobOptions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load ANPR job options", err);
      setError(
        err?.response?.data?.detail || "Failed to load ANPR job options."
      );
    } finally {
      setJobOptionsLoading(false);
    }
  };

  const loadCamerasAndBindings = async () => {
    setLoading(true);
    setError("");

    try {
      const camerasRes = await axiosInstance.get("anpr/cameras");
      const cameraRows = Array.isArray(camerasRes.data) ? camerasRes.data : [];
      setCameras(cameraRows);

      const bindingResults = await Promise.all(
        cameraRows.map(async (camera) => {
          try {
            const bindingRes = await axiosInstance.get(
              `anpr/cameras/${camera.id}/bindings`
            );
            const history = Array.isArray(bindingRes.data)
              ? bindingRes.data
              : [];

            const active =
              history.find((row) => row.is_active && !row.active_to) ||
              history.find((row) => row.is_active) ||
              null;

            return [camera.id, { active, history }];
          } catch (err) {
            console.error(
              `Failed to load bindings for camera ${camera.id}`,
              err
            );
            return [camera.id, { active: null, history: [] }];
          }
        })
      );

      setBindingsByCamera(Object.fromEntries(bindingResults));
    } catch (err) {
      console.error("Failed to load camera job binding data", err);
      setError(
        err?.response?.data?.detail || "Failed to load camera binding data."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCamerasAndBindings();
    loadJobOptions();
  }, []);

  useEffect(() => {
    const updateViewportMode = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    updateViewportMode();
    window.addEventListener("resize", updateViewportMode);

    return () => {
      window.removeEventListener("resize", updateViewportMode);
    };
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setMobileDetailsCameraId(null);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile || (!mobileDetailsCameraId && !showModal)) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobile, mobileDetailsCameraId, showModal]);

  const handleQuickJobChange = (cameraId, value) => {
    setQuickJobInputs((prev) => ({
      ...prev,
      [cameraId]: value,
    }));
  };

  const handleQuickBind = async (cameraId) => {
    const jobId = Number(quickJobInputs[cameraId] || 0);

    if (!Number.isInteger(jobId) || jobId <= 0) {
      setError("Select an ANPR job before binding.");
      return;
    }

    setSavingCameraId(cameraId);
    setError("");

    try {
      await axiosInstance.post(`anpr/cameras/${cameraId}/bindings`, {
        job_id: jobId,
        is_active: true,
      });

      setQuickJobInputs((prev) => ({
        ...prev,
        [cameraId]: "",
      }));

      await loadCamerasAndBindings();
    } catch (err) {
      console.error(`Failed to bind camera ${cameraId}`, err);
      setError(
        err?.response?.data?.detail || `Failed to bind camera ${cameraId}.`
      );
    } finally {
      setSavingCameraId(null);
    }
  };

  const openEditModal = (camera, binding) => {
    setSelectedCamera(camera);
    setSelectedBinding(binding);
    setModalForm({
      job_id: binding?.job_id ? String(binding.job_id) : "",
      active_from: toDatetimeLocalValue(binding?.active_from),
      active_to: toDatetimeLocalValue(binding?.active_to),
      latitude:
        binding?.latitude === null || binding?.latitude === undefined
          ? ""
          : String(binding.latitude),
      longitude:
        binding?.longitude === null || binding?.longitude === undefined
          ? ""
          : String(binding.longitude),
      notes: binding?.notes || "",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (modalSaving || modalUnbinding || locationBusy) return;
    setShowModal(false);
    setSelectedCamera(null);
    setSelectedBinding(null);
    setLocationBusy(false);
    setModalForm(EMPTY_MODAL_FORM);
  };

  const handleModalFieldChange = (field, value) => {
    setModalForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBindFromNow = () => {
    setModalForm((prev) => ({
      ...prev,
      active_from: toDatetimeLocalValue(new Date().toISOString()),
    }));
  };

  const handleClearEndDate = () => {
    setModalForm((prev) => ({
      ...prev,
      active_to: "",
    }));
  };

  const handleUseCurrentLocation = () => {
    setError("");

    if (!navigator.geolocation) {
      setError("Browser location is unavailable.");
      return;
    }

    setLocationBusy(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const latitude = position?.coords?.latitude;
        const longitude = position?.coords?.longitude;

        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
          setError("Current location did not return valid coordinates.");
          setLocationBusy(false);
          return;
        }

        setModalForm((prev) => ({
          ...prev,
          latitude: latitude.toFixed(6),
          longitude: longitude.toFixed(6),
        }));
        setLocationBusy(false);
      },
      (geoError) => {
        setError(
          geoError?.message ||
            "Failed to read the current browser/device location."
        );
        setLocationBusy(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const handleClearDeploymentLocation = () => {
    setModalForm((prev) => ({
      ...prev,
      latitude: "",
      longitude: "",
    }));
  };

  const handleModalSave = async () => {
    if (!selectedBinding?.id) {
      setError("No binding selected.");
      return;
    }

    const jobId = Number(modalForm.job_id || 0);
    if (!Number.isInteger(jobId) || jobId <= 0) {
      setError("Select an ANPR job.");
      return;
    }

    const activeFrom = fromDatetimeLocalValue(modalForm.active_from);
    if (!activeFrom) {
      setError("Active from is required.");
      return;
    }

    const activeTo = modalForm.active_to
      ? fromDatetimeLocalValue(modalForm.active_to)
      : null;

    const latitudeRaw = String(modalForm.latitude ?? "").trim();
    const longitudeRaw = String(modalForm.longitude ?? "").trim();

    if ((latitudeRaw === "") !== (longitudeRaw === "")) {
      setError("Latitude and longitude must be supplied together.");
      return;
    }

    let latitude = null;
    let longitude = null;

    if (latitudeRaw !== "" && longitudeRaw !== "") {
      latitude = Number(latitudeRaw);
      longitude = Number(longitudeRaw);

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        setError("Latitude and longitude must be valid numbers.");
        return;
      }

      if (latitude < -90 || latitude > 90) {
        setError("Latitude must be between -90 and 90.");
        return;
      }

      if (longitude < -180 || longitude > 180) {
        setError("Longitude must be between -180 and 180.");
        return;
      }
    }

    setModalSaving(true);
    setError("");

    try {
      await axiosInstance.patch(
        `anpr/cameras/bindings/${selectedBinding.id}`,
        {
          job_id: jobId,
          is_active: true,
          active_from: activeFrom,
          active_to: activeTo,
          latitude,
          longitude,
          notes: modalForm.notes?.trim() || null,
        }
      );

      await loadCamerasAndBindings();
      closeModal();
    } catch (err) {
      console.error("Failed to update binding", err);
      setError(err?.response?.data?.detail || "Failed to update binding.");
    } finally {
      setModalSaving(false);
    }
  };

  const handleModalUnbind = async () => {
    if (!selectedBinding?.id) {
      setError("No binding selected.");
      return;
    }

    setModalUnbinding(true);
    setError("");

    try {
      await axiosInstance.post(
        `anpr/cameras/bindings/${selectedBinding.id}/unbind`
      );
      await loadCamerasAndBindings();
      closeModal();
    } catch (err) {
      console.error("Failed to unbind camera binding", err);
      setError(err?.response?.data?.detail || "Failed to unbind binding.");
    } finally {
      setModalUnbinding(false);
    }
  };

  const sortedCameras = useMemo(() => {
    return [...cameras].sort((a, b) => {
      const aId = Number(a?.id || 0);
      const bId = Number(b?.id || 0);
      return aId - bId;
    });
  }, [cameras]);

  const mobileDetailsCamera = useMemo(
    () =>
      sortedCameras.find(
        (camera) => Number(camera.id) === Number(mobileDetailsCameraId)
      ) || null,
    [sortedCameras, mobileDetailsCameraId]
  );

  const mobileDetailsBindingData = mobileDetailsCamera
    ? bindingsByCamera[mobileDetailsCamera.id] || { active: null, history: [] }
    : { active: null, history: [] };

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const gridHost = root.closest(".react-grid-item");
    if (!gridHost) return undefined;

    const previousHeight = gridHost.style.height;
    const previousMinHeight = gridHost.style.minHeight;

    let frameId = null;
    let observer = null;

    const restoreDesktopHeight = () => {
      gridHost.style.height = previousHeight;
      gridHost.style.minHeight = previousMinHeight;
    };

    const syncMobileHostHeight = () => {
      if (frameId) cancelAnimationFrame(frameId);

      frameId = requestAnimationFrame(() => {
        if (!isMobile) {
          restoreDesktopHeight();
          return;
        }

        const hostRect = gridHost.getBoundingClientRect();
        const rootRect = root.getBoundingClientRect();
        const topOffset = Math.max(0, rootRect.top - hostRect.top);
        const requiredHeight = Math.ceil(topOffset + root.scrollHeight + 8);

        gridHost.style.height = `${requiredHeight}px`;
        gridHost.style.minHeight = `${requiredHeight}px`;
      });
    };

    syncMobileHostHeight();
    window.addEventListener("resize", syncMobileHostHeight);

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(syncMobileHostHeight);
      observer.observe(root);
    }

    return () => {
      window.removeEventListener("resize", syncMobileHostHeight);
      if (observer) observer.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
      restoreDesktopHeight();
    };
  }, [
    isMobile,
    sortedCameras.length,
    bindingsByCamera,
    error,
    jobOptionsLoading,
    savingCameraId,
  ]);

  const touchButtonStyle = (style) => ({
    ...style,
    ...(isMobile
      ? {
          width: "100%",
          minHeight: "44px",
          whiteSpace: "normal",
        }
      : {}),
  });

  const renderCurrentBinding = (camera, active, compact = false) => {
    if (!active) {
      return (
        <div style={{ fontSize: "0.92rem", color: "#666" }}>
          No active or scheduled binding.
        </div>
      );
    }

    const bindingStatus = getBindingStatus(active);

    if (compact) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            minWidth: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
              minWidth: 0,
            }}
          >
            <strong style={{ minWidth: 0, overflowWrap: "anywhere" }}>
              {active.job_display || `Job #${active.job_id}`}
            </strong>
            <span
              style={{
                display: "inline-block",
                padding: "4px 10px",
                borderRadius: "999px",
                fontWeight: 700,
                fontSize: "0.8rem",
                ...getStatusPillStyle(bindingStatus),
              }}
            >
              {bindingStatus}
            </span>
          </div>
          <div style={{ color: "#666", fontSize: "0.84rem" }}>
            {formatDateTime(active.active_from)} → {formatDateTime(active.active_to)}
          </div>
          <div style={{ color: "#666", fontSize: "0.84rem" }}>
            Camera: {formatBindingLocation(active)}
          </div>
        </div>
      );
    }

    return (
      <div
        style={{
          fontSize: "0.92rem",
          color: "#333",
          lineHeight: 1.55,
          overflowWrap: "anywhere",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "6px",
            flexWrap: "wrap",
          }}
        >
          <strong>{active.job_display || `Job #${active.job_id}`}</strong>
          <span
            style={{
              display: "inline-block",
              padding: "4px 10px",
              borderRadius: "999px",
              fontWeight: 700,
              fontSize: "0.8rem",
              ...getStatusPillStyle(bindingStatus),
            }}
          >
            {bindingStatus}
          </span>
        </div>
        <div><strong>Job status:</strong> {active.job_status || "—"}</div>
        <div><strong>Type:</strong> {active.job_type_name || "—"}</div>
        <div><strong>From:</strong> {formatDateTime(active.active_from)}</div>
        <div><strong>To:</strong> {formatDateTime(active.active_to)}</div>
        <div><strong>Camera location:</strong> {formatBindingLocation(active)}</div>
        <div><strong>WC:</strong> {active.week_commencing || "—"}</div>
        <div><strong>Notes:</strong> {active.notes || "—"}</div>

        <div
          style={{
            marginTop: "10px",
            display: "flex",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            onClick={() => openEditModal(camera, active)}
            style={touchButtonStyle(buttonStyle(theme, "general"))}
            onMouseEnter={(e) => applyHover(e, theme, "general")}
            onMouseLeave={(e) => applyNormal(e, theme, "general")}
            onMouseDown={(e) => applyClick(e, theme, "general")}
            onMouseUp={(e) => applyHover(e, theme, "general")}
          >
            Edit Binding
          </button>
        </div>
      </div>
    );
  };

  const renderQuickBind = (camera, active, mobile = false) => {
    const isSaving = savingCameraId === camera.id;

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: mobile ? "1fr" : "minmax(220px, 1fr) auto",
          gap: "10px",
          alignItems: "center",
        }}
      >
        <select
          value={quickJobInputs[camera.id] || ""}
          onChange={(e) => handleQuickJobChange(camera.id, e.target.value)}
          style={{
            ...inputStyle,
            ...(mobile ? { minHeight: "44px", fontSize: "16px" } : {}),
          }}
          disabled={jobOptionsLoading || isSaving}
        >
          <option value="">
            {jobOptionsLoading ? "Loading ANPR jobs..." : "Select ANPR job"}
          </option>
          {jobOptions.map((job) => (
            <option key={job.id} value={job.id}>
              {job.job_display}
            </option>
          ))}
        </select>

        <button
          type="button"
          disabled={isSaving || jobOptionsLoading}
          onClick={() => handleQuickBind(camera.id)}
          style={
            mobile
              ? touchButtonStyle(
                  buttonStyle(
                    theme,
                    "action",
                    isSaving || jobOptionsLoading
                  )
                )
              : buttonStyle(
                  theme,
                  "action",
                  isSaving || jobOptionsLoading
                )
          }
          onMouseEnter={(e) =>
            applyHover(
              e,
              theme,
              "action",
              isSaving || jobOptionsLoading
            )
          }
          onMouseLeave={(e) => applyNormal(e, theme, "action")}
          onMouseDown={(e) =>
            applyClick(
              e,
              theme,
              "action",
              isSaving || jobOptionsLoading
            )
          }
          onMouseUp={(e) =>
            applyHover(
              e,
              theme,
              "action",
              isSaving || jobOptionsLoading
            )
          }
        >
          {isSaving
            ? "Saving..."
            : active
              ? "Rebind From Now"
              : "Bind From Now"}
        </button>
      </div>
    );
  };

  const renderHistory = (history, mobile = false) => {
    if (history.length === 0) {
      return (
        <div style={{ fontSize: "0.92rem", color: "#666" }}>
          No binding history found.
        </div>
      );
    }

    if (mobile) {
      return (
        <div style={{ display: "grid", gap: "10px" }}>
          {history.slice(0, 5).map((row) => {
            const rowStatus = getBindingStatus(row);
            return (
              <div
                key={row.id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px",
                  backgroundColor: "#fff",
                  overflow: "hidden",
                }}
              >
                {[
                  ["Job", row.job_display || `#${row.job_id}`],
                  ["From", formatDateTime(row.active_from)],
                  ["To", formatDateTime(row.active_to)],
                  ["Camera", formatBindingLocation(row)],
                  ["Notes", row.notes || "—"],
                ].map(([label, value], index) => (
                  <div
                    key={label}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "78px minmax(0, 1fr)",
                      gap: "10px",
                      padding: "9px 10px",
                      borderBottom:
                        index === 4 ? "none" : "1px solid #f3f4f6",
                      minWidth: 0,
                    }}
                  >
                    <div style={mobileLabelStyle}>{label}</div>
                    <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                      {value}
                    </div>
                  </div>
                ))}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "78px minmax(0, 1fr)",
                    gap: "10px",
                    padding: "9px 10px",
                    borderTop: "1px solid #f3f4f6",
                  }}
                >
                  <div style={mobileLabelStyle}>Status</div>
                  <div>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "4px 10px",
                        borderRadius: "999px",
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        ...getStatusPillStyle(rowStatus),
                      }}
                    >
                      {rowStatus}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div
        style={{
          overflowX: "auto",
          border: "1px solid #ececec",
          borderRadius: "10px",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "0.9rem",
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "#f7f7f7", textAlign: "left" }}>
              <th style={{ padding: "10px 12px" }}>Job</th>
              <th style={{ padding: "10px 12px" }}>Status</th>
              <th style={{ padding: "10px 12px" }}>From</th>
              <th style={{ padding: "10px 12px" }}>To</th>
              <th style={{ padding: "10px 12px" }}>Camera Location</th>
              <th style={{ padding: "10px 12px" }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {history.slice(0, 5).map((row) => {
              const rowStatus = getBindingStatus(row);
              return (
                <tr key={row.id} style={{ borderTop: "1px solid #efefef" }}>
                  <td style={{ padding: "10px 12px" }}>
                    {row.job_display || `#${row.job_id}`}
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "4px 10px",
                        borderRadius: "999px",
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        ...getStatusPillStyle(rowStatus),
                      }}
                    >
                      {rowStatus}
                    </span>
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    {formatDateTime(row.active_from)}
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    {formatDateTime(row.active_to)}
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    {formatBindingLocation(row)}
                  </td>
                  <td style={{ padding: "10px 12px" }}>{row.notes || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: "8px 4px", color: "#444" }}>
        Loading camera bindings...
      </div>
    );
  }

  return (
    <>
      <div
        ref={rootRef}
        style={{
          height: isMobile ? "auto" : "100%",
          minHeight: 0,
          width: "100%",
          overflow: isMobile ? "visible" : undefined,
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        {error && (
          <div
            style={{
              backgroundColor: "#fff4f4",
              border: "1px solid #f0caca",
              color: "#a10000",
              borderRadius: "8px",
              padding: "10px 12px",
              fontSize: "0.95rem",
              overflowWrap: "anywhere",
            }}
          >
            {error}
          </div>
        )}

        {sortedCameras.length === 0 ? (
          <div style={{ color: "#555", padding: "8px 4px" }}>
            No cameras found for this tenant.
          </div>
        ) : isMobile ? (
          <div style={{ display: "grid", gap: "12px" }}>
            {sortedCameras.map((camera) => {
              const bindingData = bindingsByCamera[camera.id] || {
                active: null,
                history: [],
              };
              const active = bindingData.active;

              return (
                <div
                  key={camera.id}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "12px",
                    backgroundColor: "#fff",
                    padding: "12px",
                    display: "grid",
                    gap: "12px",
                    minWidth: 0,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "1rem",
                        fontWeight: 800,
                        color: theme.accent_color || "#000",
                        marginBottom: "6px",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {camera.camera_name || `Camera ${camera.id}`}
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "80px minmax(0, 1fr)",
                        gap: "5px 10px",
                        fontSize: "0.88rem",
                        color: "#444",
                        lineHeight: 1.4,
                      }}
                    >
                      <strong>ID</strong><span>{camera.id}</span>
                      <strong>Serial</strong><span style={{ overflowWrap: "anywhere" }}>{camera.serial_no || "—"}</span>
                      <strong>Model</strong><span style={{ overflowWrap: "anywhere" }}>{camera.model_no || "—"}</span>
                      <strong>Location</strong><span style={{ overflowWrap: "anywhere" }}>{camera.location_name || "—"}</span>
                      <strong>Status</strong><span>{camera.is_active ? "Active" : "Inactive"}</span>
                    </div>

                    {!camera.has_ingest_key && (
                      <div
                        style={{
                          marginTop: "10px",
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "6px 10px",
                          borderRadius: "999px",
                          backgroundColor: "#fff7ed",
                          border: "1px solid #fdba74",
                          color: "#9a3412",
                          fontWeight: 700,
                          fontSize: "0.82rem",
                        }}
                      >
                        No ingest key configured
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      borderTop: "1px solid #ececec",
                      paddingTop: "10px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.78rem",
                        fontWeight: 800,
                        color: "#666",
                        marginBottom: "6px",
                        textTransform: "uppercase",
                        letterSpacing: "0.035em",
                      }}
                    >
                      Current Binding
                    </div>
                    {renderCurrentBinding(camera, active, true)}
                  </div>

                  <button
                    type="button"
                    onClick={() => setMobileDetailsCameraId(camera.id)}
                    style={touchButtonStyle(buttonStyle(theme, "action"))}
                    onMouseEnter={(e) => applyHover(e, theme, "action")}
                    onMouseLeave={(e) => applyNormal(e, theme, "action")}
                    onMouseDown={(e) => applyClick(e, theme, "action")}
                    onMouseUp={(e) => applyHover(e, theme, "action")}
                  >
                    Binding Details
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {sortedCameras.map((camera) => {
              const bindingData = bindingsByCamera[camera.id] || {
                active: null,
                history: [],
              };
              const active = bindingData.active;
              const history = bindingData.history || [];

              return (
                <div
                  key={camera.id}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "12px",
                    backgroundColor: "#fff",
                    padding: "14px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "minmax(0, 1fr) minmax(280px, 340px)",
                      gap: "12px",
                      alignItems: "start",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: "1rem",
                          fontWeight: 700,
                          color: theme.accent_color || "#000",
                          marginBottom: "6px",
                        }}
                      >
                        {camera.camera_name || `Camera ${camera.id}`}
                      </div>

                      <div
                        style={{
                          fontSize: "0.92rem",
                          color: "#444",
                          lineHeight: 1.5,
                        }}
                      >
                        <div><strong>ID:</strong> {camera.id}</div>
                        <div><strong>Serial:</strong> {camera.serial_no || "—"}</div>
                        <div><strong>MAC:</strong> {camera.mac_address || "—"}</div>
                        <div><strong>Model:</strong> {camera.model_no || "—"}</div>
                        <div><strong>Location:</strong> {camera.location_name || "—"}</div>
                        <div><strong>Status:</strong> {camera.is_active ? "Active" : "Inactive"}</div>
                      </div>

                      {!camera.has_ingest_key && (
                        <div
                          style={{
                            marginTop: "10px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "6px 10px",
                            borderRadius: "999px",
                            backgroundColor: "#fff7ed",
                            border: "1px solid #fdba74",
                            color: "#9a3412",
                            fontWeight: 700,
                            fontSize: "0.85rem",
                          }}
                        >
                          No ingest key configured
                        </div>
                      )}
                    </div>

                    <div
                      style={{
                        border: "1px solid #ececec",
                        borderRadius: "10px",
                        padding: "10px 12px",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          marginBottom: "8px",
                          color: "#222",
                        }}
                      >
                        Current Binding
                      </div>
                      {renderCurrentBinding(camera, active, false)}
                    </div>
                  </div>

                  {renderQuickBind(camera, active, false)}

                  <div>
                    <div
                      style={{
                        fontWeight: 700,
                        color: "#222",
                        marginBottom: "8px",
                      }}
                    >
                      Recent Binding History
                    </div>
                    {renderHistory(history, false)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {isMobile && mobileDetailsCamera && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9998,
            backgroundColor: "rgba(0,0,0,0.45)",
            display: "flex",
            justifyContent: "flex-end",
          }}
          onClick={() => setMobileDetailsCameraId(null)}
          role="presentation"
        >
          <div
            style={{
              width: "min(94vw, 460px)",
              height: "100dvh",
              backgroundColor: "#fff",
              boxShadow: "-10px 0 30px rgba(0,0,0,0.22)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`Binding details for ${mobileDetailsCamera.camera_name || `Camera ${mobileDetailsCamera.id}`}`}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: "12px",
                padding: "14px",
                borderBottom: "1px solid #e5e7eb",
                flexShrink: 0,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: "1rem",
                    fontWeight: 800,
                    color: theme.accent_color || "#000",
                    overflowWrap: "anywhere",
                  }}
                >
                  {mobileDetailsCamera.camera_name ||
                    `Camera ${mobileDetailsCamera.id}`}
                </div>
                <div style={{ color: "#666", fontSize: "0.85rem", marginTop: "3px" }}>
                  Binding Details
                </div>
              </div>

              <button
                type="button"
                onClick={() => setMobileDetailsCameraId(null)}
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "999px",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "20px",
                  fontWeight: 700,
                  backgroundColor: "#f3f4f6",
                  color: "#333",
                  flexShrink: 0,
                }}
                title="Close binding details"
                aria-label="Close binding details"
              >
                ×
              </button>
            </div>

            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                padding: "14px",
                display: "grid",
                gap: "16px",
                WebkitOverflowScrolling: "touch",
              }}
            >
              {error && (
                <div
                  style={{
                    backgroundColor: "#fff4f4",
                    border: "1px solid #f0caca",
                    color: "#a10000",
                    borderRadius: "8px",
                    padding: "10px 12px",
                    fontSize: "0.9rem",
                    overflowWrap: "anywhere",
                  }}
                >
                  {error}
                </div>
              )}

              <section>
                <div
                  style={{
                    fontWeight: 800,
                    color: "#222",
                    marginBottom: "8px",
                  }}
                >
                  Current Binding
                </div>
                <div
                  style={{
                    border: "1px solid #ececec",
                    borderRadius: "10px",
                    padding: "12px",
                    backgroundColor: "#fafafa",
                  }}
                >
                  {renderCurrentBinding(
                    mobileDetailsCamera,
                    mobileDetailsBindingData.active,
                    false
                  )}
                </div>
              </section>

              <section>
                <div
                  style={{
                    fontWeight: 800,
                    color: "#222",
                    marginBottom: "8px",
                  }}
                >
                  Quick Bind / Rebind
                </div>
                {renderQuickBind(
                  mobileDetailsCamera,
                  mobileDetailsBindingData.active,
                  true
                )}
              </section>

              <section style={{ paddingBottom: "10px" }}>
                <div
                  style={{
                    fontWeight: 800,
                    color: "#222",
                    marginBottom: "8px",
                  }}
                >
                  Recent Binding History
                </div>
                {renderHistory(mobileDetailsBindingData.history || [], true)}
              </section>
            </div>
          </div>
        </div>
      )}

      {showModal && selectedCamera && selectedBinding && (
        <div
          style={{
            ...modalOverlayStyle,
            padding: isMobile ? 0 : modalOverlayStyle.padding,
            alignItems: isMobile ? "stretch" : modalOverlayStyle.alignItems,
          }}
          onClick={closeModal}
        >
          <div
            style={{
              ...modalCardStyle,
              maxWidth: isMobile ? "100%" : modalCardStyle.maxWidth,
              width: "100%",
              height: isMobile ? "100dvh" : "auto",
              maxHeight: isMobile ? "100dvh" : modalCardStyle.maxHeight,
              borderRadius: isMobile ? 0 : modalCardStyle.borderRadius,
              padding: isMobile ? "14px" : modalCardStyle.padding,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "start",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "1.05rem",
                    fontWeight: 800,
                    color: theme.accent_color || "#000",
                    marginBottom: "4px",
                  }}
                >
                  Edit Camera Binding
                </div>
                <div
                  style={{
                    color: "#555",
                    fontSize: "0.92rem",
                    lineHeight: 1.5,
                  }}
                >
                  <div>
                    <strong>Camera:</strong>{" "}
                    {selectedCamera.camera_name ||
                      `Camera ${selectedCamera.id}`}
                  </div>
                  <div>
                    <strong>Current job:</strong>{" "}
                    {selectedBinding.job_display ||
                      `Job #${selectedBinding.job_id}`}
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={closeModal}
                style={{
                  width: isMobile ? "44px" : "34px",
                  height: isMobile ? "44px" : "34px",
                  borderRadius: "999px",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "18px",
                  fontWeight: 700,
                  backgroundColor: "#f3f4f6",
                  color: "#333",
                  flexShrink: 0,
                }}
                title="Close"
              >
                ×
              </button>
            </div>

            <div style={{ display: "grid", gap: "14px" }}>
              <div>
                <label
                  style={{
                    display: "block",
                    fontWeight: 700,
                    marginBottom: "6px",
                  }}
                >
                  ANPR Job
                </label>
                <select
                  value={modalForm.job_id}
                  onChange={(e) =>
                    handleModalFieldChange("job_id", e.target.value)
                  }
                  style={{
                    ...inputStyle,
                    ...(isMobile
                      ? { minHeight: "44px", fontSize: "16px" }
                      : {}),
                  }}
                >
                  <option value="">Select ANPR job</option>
                  {jobOptions.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.job_display}
                    </option>
                  ))}
                </select>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : "repeat(2, minmax(0, 1fr))",
                  gap: "12px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontWeight: 700,
                      marginBottom: "6px",
                    }}
                  >
                    Active From
                  </label>
                  <input
                    type="datetime-local"
                    value={modalForm.active_from}
                    onChange={(e) =>
                      handleModalFieldChange("active_from", e.target.value)
                    }
                    style={{
                      ...inputStyle,
                      ...(isMobile
                        ? { minHeight: "44px", fontSize: "16px" }
                        : {}),
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontWeight: 700,
                      marginBottom: "6px",
                    }}
                  >
                    Active To
                  </label>
                  <input
                    type="datetime-local"
                    value={modalForm.active_to}
                    onChange={(e) =>
                      handleModalFieldChange("active_to", e.target.value)
                    }
                    style={{
                      ...inputStyle,
                      ...(isMobile
                        ? { minHeight: "44px", fontSize: "16px" }
                        : {}),
                    }}
                  />
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: "8px",
                  flexWrap: "wrap",
                  width: isMobile ? "100%" : "auto",
                }}
              >
                <button
                  type="button"
                  onClick={handleBindFromNow}
                  style={touchButtonStyle(buttonStyle(theme, "general"))}
                  onMouseEnter={(e) => applyHover(e, theme, "general")}
                  onMouseLeave={(e) => applyNormal(e, theme, "general")}
                  onMouseDown={(e) => applyClick(e, theme, "general")}
                  onMouseUp={(e) => applyHover(e, theme, "general")}
                >
                  Bind From Now
                </button>

                <button
                  type="button"
                  onClick={handleClearEndDate}
                  style={touchButtonStyle(buttonStyle(theme, "general"))}
                  onMouseEnter={(e) => applyHover(e, theme, "general")}
                  onMouseLeave={(e) => applyNormal(e, theme, "general")}
                  onMouseDown={(e) => applyClick(e, theme, "general")}
                  onMouseUp={(e) => applyHover(e, theme, "general")}
                >
                  Clear End Date
                </button>
              </div>

              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px",
                  padding: "12px",
                  backgroundColor: "#fafafa",
                }}
              >
                <div
                  style={{
                    fontWeight: 800,
                    color: "#222",
                    marginBottom: "4px",
                  }}
                >
                  Camera Deployment Location
                </div>

                <div
                  style={{
                    color: "#666",
                    fontSize: "0.88rem",
                    lineHeight: 1.45,
                    marginBottom: "12px",
                  }}
                >
                  Physical position of this camera for this job binding. These
                  coordinates are used as the ANPR sighting fallback when the
                  camera ingest does not provide GPS.
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr"
                      : "repeat(2, minmax(0, 1fr))",
                    gap: "12px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontWeight: 700,
                        marginBottom: "6px",
                      }}
                    >
                      Latitude
                    </label>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.000001"
                      min="-90"
                      max="90"
                      value={modalForm.latitude}
                      onChange={(e) =>
                        handleModalFieldChange("latitude", e.target.value)
                      }
                      style={{
                        ...inputStyle,
                        ...(isMobile
                          ? { minHeight: "44px", fontSize: "16px" }
                          : {}),
                      }}
                      placeholder="e.g. 51.752013"
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontWeight: 700,
                        marginBottom: "6px",
                      }}
                    >
                      Longitude
                    </label>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.000001"
                      min="-180"
                      max="180"
                      value={modalForm.longitude}
                      onChange={(e) =>
                        handleModalFieldChange("longitude", e.target.value)
                      }
                      style={{
                        ...inputStyle,
                        ...(isMobile
                          ? { minHeight: "44px", fontSize: "16px" }
                          : {}),
                      }}
                      placeholder="e.g. -1.257849"
                    />
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    gap: "8px",
                    flexWrap: "wrap",
                    marginTop: "10px",
                  }}
                >
                  <button
                    type="button"
                    onClick={handleUseCurrentLocation}
                    disabled={locationBusy || modalSaving || modalUnbinding}
                    style={touchButtonStyle(
                      buttonStyle(
                        theme,
                        "general",
                        locationBusy || modalSaving || modalUnbinding
                      )
                    )}
                    onMouseEnter={(e) =>
                      applyHover(
                        e,
                        theme,
                        "general",
                        locationBusy || modalSaving || modalUnbinding
                      )
                    }
                    onMouseLeave={(e) => applyNormal(e, theme, "general")}
                    onMouseDown={(e) =>
                      applyClick(
                        e,
                        theme,
                        "general",
                        locationBusy || modalSaving || modalUnbinding
                      )
                    }
                    onMouseUp={(e) =>
                      applyHover(
                        e,
                        theme,
                        "general",
                        locationBusy || modalSaving || modalUnbinding
                      )
                    }
                  >
                    {locationBusy ? "Locating..." : "Use Current Location"}
                  </button>

                  <button
                    type="button"
                    onClick={handleClearDeploymentLocation}
                    disabled={locationBusy || modalSaving || modalUnbinding}
                    style={touchButtonStyle(
                      buttonStyle(
                        theme,
                        "general",
                        locationBusy || modalSaving || modalUnbinding
                      )
                    )}
                    onMouseEnter={(e) =>
                      applyHover(
                        e,
                        theme,
                        "general",
                        locationBusy || modalSaving || modalUnbinding
                      )
                    }
                    onMouseLeave={(e) => applyNormal(e, theme, "general")}
                    onMouseDown={(e) =>
                      applyClick(
                        e,
                        theme,
                        "general",
                        locationBusy || modalSaving || modalUnbinding
                      )
                    }
                    onMouseUp={(e) =>
                      applyHover(
                        e,
                        theme,
                        "general",
                        locationBusy || modalSaving || modalUnbinding
                      )
                    }
                  >
                    Clear Location
                  </button>
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontWeight: 700,
                    marginBottom: "6px",
                  }}
                >
                  Notes
                </label>
                <textarea
                  value={modalForm.notes}
                  onChange={(e) =>
                    handleModalFieldChange("notes", e.target.value)
                  }
                  style={{
                    ...textareaStyle,
                    ...(isMobile ? { fontSize: "16px" } : {}),
                  }}
                  placeholder="Optional notes for this deployment / binding"
                />
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  justifyContent: "space-between",
                  gap: "12px",
                  flexWrap: "wrap",
                  marginTop: "6px",
                }}
              >
                <button
                  type="button"
                  onClick={handleModalUnbind}
                  disabled={modalUnbinding || modalSaving || locationBusy}
                  style={touchButtonStyle(
                    buttonStyle(
                      theme,
                      "general",
                      modalUnbinding || modalSaving || locationBusy
                    )
                  )}
                  onMouseEnter={(e) =>
                    applyHover(
                      e,
                      theme,
                      "general",
                      modalUnbinding || modalSaving || locationBusy
                    )
                  }
                  onMouseLeave={(e) => applyNormal(e, theme, "general")}
                  onMouseDown={(e) =>
                    applyClick(
                      e,
                      theme,
                      "general",
                      modalUnbinding || modalSaving || locationBusy
                    )
                  }
                  onMouseUp={(e) =>
                    applyHover(
                      e,
                      theme,
                      "general",
                      modalUnbinding || modalSaving || locationBusy
                    )
                  }
                >
                  {modalUnbinding ? "Unbinding..." : "Unbind"}
                </button>

                <div
                  style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    gap: "8px",
                    flexWrap: "wrap",
                    width: isMobile ? "100%" : "auto",
                  }}
                >
                  <button
                    type="button"
                    onClick={closeModal}
                    disabled={modalSaving || modalUnbinding || locationBusy}
                    style={touchButtonStyle(
                      buttonStyle(
                        theme,
                        "general",
                        modalSaving || modalUnbinding || locationBusy
                      )
                    )}
                    onMouseEnter={(e) =>
                      applyHover(
                        e,
                        theme,
                        "general",
                        modalSaving || modalUnbinding || locationBusy
                      )
                    }
                    onMouseLeave={(e) => applyNormal(e, theme, "general")}
                    onMouseDown={(e) =>
                      applyClick(
                        e,
                        theme,
                        "general",
                        modalSaving || modalUnbinding || locationBusy
                      )
                    }
                    onMouseUp={(e) =>
                      applyHover(
                        e,
                        theme,
                        "general",
                        modalSaving || modalUnbinding || locationBusy
                      )
                    }
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={handleModalSave}
                    disabled={modalSaving || modalUnbinding || locationBusy}
                    style={touchButtonStyle(
                      buttonStyle(
                        theme,
                        "action",
                        modalSaving || modalUnbinding || locationBusy
                      )
                    )}
                    onMouseEnter={(e) =>
                      applyHover(
                        e,
                        theme,
                        "action",
                        modalSaving || modalUnbinding || locationBusy
                      )
                    }
                    onMouseLeave={(e) => applyNormal(e, theme, "action")}
                    onMouseDown={(e) =>
                      applyClick(
                        e,
                        theme,
                        "action",
                        modalSaving || modalUnbinding || locationBusy
                      )
                    }
                    onMouseUp={(e) =>
                      applyHover(
                        e,
                        theme,
                        "action",
                        modalSaving || modalUnbinding || locationBusy
                      )
                    }
                  >
                    {modalSaving ? "Saving..." : "Save Binding"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CameraJobBindingWidget;
